import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from '../utils/toast';

// Predefined knowledge base
const SITE_KNOWLEDGE = {
  features: {
    'dashboard': {
      description: 'The central hub showing all available applications and tools. You can find AI-powered apps marked with "Gen AI" badges and quick access to frequently used features.',
      location: '/dashboard',
      tips: ['Use the "Customize Dashboard" button to select which apps you want to see', 'Check the notification badges for updates', 'AI-powered apps are marked with special badges']
    },
    'grant-finder': {
      description: 'An AI-powered tool to discover grants tailored to your organization. Search through thousands of funding opportunities.',
      location: '/grant-finder',
      tips: ['Use filters to narrow down grant opportunities', 'Save interesting grants for later', 'Set up email alerts for new matches']
    },
    'rfp-finder': {
      description: 'Find and filter RFP opportunities that match your organization\'s capabilities.',
      location: '/rfp-finder',
      tips: ['Set up your organization profile for better matches', 'Use the save feature to track interesting RFPs', 'Export RFP details to share with your team']
    },
    'powerpoint-generator': {
      description: 'Create professional presentations with AI assistance, including smart layouts and design suggestions.',
      location: '/powerpoint-generator',
      tips: ['Start with a clear outline', 'Use the voice recording feature for content', 'Preview slides before generating the final version']
    },
    'insights': {
      description: 'View analytics dashboard showing platform usage statistics and AI agent performance metrics.',
      location: '/insights',
      tips: ['Check the trends section for usage patterns', 'Export reports for team meetings', 'Set up custom dashboard views']
    },
    'settings': {
      description: 'Manage your profile, notifications, and integration settings.',
      location: '/settings',
      tips: ['Review notification settings regularly', 'Keep your profile information updated', 'Check available integrations']
    }
  },
  general_tips: [
    'Beta features are marked with a special badge and may have limited functionality',
    'The platform works best with Chrome or Edge browsers',
    'Use the search function in each tool to quickly find what you need',
    'Most AI-powered features allow you to edit and refine the generated content',
    'Save your work regularly, especially in the content generation tools'
  ],
  common_questions: {
    'how to start': 'Start by exploring the Dashboard where you\'ll find all available tools. Look for the "Gen AI" badge for AI-powered features.',
    'new features': 'Check the Dashboard for apps marked with "New" badges. Beta features are actively being developed and improved.',
    'get help': 'You can find help by: 1) Using this chat assistant, 2) Checking the help docs in Settings, or 3) Contacting support.',
    'customize': 'Use the "Customize Dashboard" button on the Dashboard to select which apps you want to see.',
    'ai features': 'Look for the "Gen AI" badge on tools. These include Grant Finder, RFP Finder, PowerPoint Generator, and more.',
    'save work': 'Most tools have an auto-save feature. You can also manually save using the save button or Ctrl/Cmd + S.',
    'export': 'Look for the export button (usually in the top right) to download or share your work.',
    'beta access': 'Beta features are available to all users but may have limited functionality as we improve them.',
    'keyboard shortcuts': 'Common shortcuts: Ctrl/Cmd + S to save, Esc to close modals, Enter to submit forms.',
    'data security': 'All data is encrypted and stored securely. Check Settings > Security for more details.'
  }
};

// Helper function to find relevant responses
function findRelevantResponses(query) {
  query = query.toLowerCase();
  let responses = [];

  // Check for feature-specific questions
  for (const [feature, info] of Object.entries(SITE_KNOWLEDGE.features)) {
    if (query.includes(feature)) {
      responses.push(info.description);
      responses.push('Tips for ' + feature + ':');
      responses.push(...info.tips.map(tip => '• ' + tip));
      return responses.join('\n\n');
    }
  }

  // Check common questions
  for (const [question, answer] of Object.entries(SITE_KNOWLEDGE.common_questions)) {
    if (query.includes(question)) {
      return answer;
    }
  }

  // If no specific match, provide general help
  if (query.includes('help') || query.includes('how') || query.includes('what')) {
    return `I can help you with:
    
• Navigating the platform and finding features
• Understanding how to use specific tools
• Getting started with AI-powered features
• Customizing your dashboard
• Accessing help and support

What would you like to know more about?`;
  }

  // Default response
  return `I'm here to help you navigate and use the platform effectively. You can ask me about:

• Specific features like Grant Finder, RFP Finder, or PowerPoint Generator
• How to get started with any tool
• Tips for using AI-powered features
• General platform navigation
• Common tasks like saving work or exporting

What would you like to learn about?`;
}

export function Ethan() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm Ethan, your platform assistant. I can help you navigate features and get the most out of our tools. What would you like to know?" }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef(null);
  const location = useLocation();
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const toggleAssistant = () => {
    setIsOpen(!isOpen);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isProcessing) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsProcessing(true);

    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      // Get response from knowledge base
      const response = findRelevantResponses(userMessage);
      
      // Add slight delay to feel more natural
      setTimeout(() => {
        setMessages(prev => [...prev, { role: 'assistant', content: response }]);
        setIsProcessing(false);
      }, 500);
      
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Sorry, I had trouble processing your request. Please try again.');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I apologize, but I'm having trouble processing your request right now. Could you try rephrasing your question?" 
      }]);
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chat button */}
      <button
        onClick={toggleAssistant}
        className="bg-primary-600 hover:bg-primary-700 text-white rounded-full p-3 shadow-lg transition-all duration-200 flex items-center justify-center"
      >
        {isOpen ? (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
      </button>
      
      {/* Chat Interface */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-96 h-[32rem] bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-t-xl">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center mr-3">
                <span className="text-lg">E</span>
              </div>
              <div>
                <span className="font-medium">Ethan</span>
                <div className="text-xs text-primary-100 flex items-center">
                  <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-1"></span>
                  Online
                </div>
              </div>
            </div>
            <button 
              onClick={toggleAssistant}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
              >
                <div className={`max-w-[80%] rounded-lg p-3 ${
                  message.role === 'assistant'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'bg-primary-600 text-white'
                }`}>
                  <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                </div>
              </div>
            ))}
            {isProcessing && (
              <div className="flex justify-start">
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input area */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 bg-white">
            <div className="flex items-center">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="submit"
                disabled={isProcessing || !inputMessage.trim()}
                className="ml-2 p-2 bg-primary-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
} 